import React from 'react'
import Image from 'next/image'
import cx from 'classnames'
import styles from './CardFullWidth.module.scss'
import { Container } from 'react-grid-system'
import { Button, Divider, Heading, Paragraph } from '~elements'
import { ButtonGroup, Calendar } from '~modules'
import { AssetProps, ButtonProps, CalendarProps, WidgetSectionProps } from '~types'
import { useWindowSize } from 'utils/hooks'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { getImageUrl } from 'utils/assets/getImageUrl'
import { getGtmLinkPosition } from '~analyticsUtils'

interface CardFullWidthProps {
  imageXl?: AssetProps
  imageMd?: AssetProps
  imageSm?: AssetProps
  buttonGroup?: Array<ButtonProps>
  ctaSection?: Array<ctaSectionTitle | WidgetSectionProps>
  description: {
    html: string
  }
  disclaimer?: {
    html: string
  }
  headline: string
  subheadline: string
  calendar?: CalendarProps
  cta?: ButtonProps
  showDivider: boolean
}

interface ctaSectionTitle {
  title: string
}

const CardFullWidth = ({
  description,
  disclaimer,
  buttonGroup,
  headline,
  subheadline = '',
  calendar,
  cta,
  ctaSection,
  showDivider,
  imageXl,
  imageMd,
  imageSm
}: CardFullWidthProps) => {
  const { isBreakpoint: isBelowBreakpointSm, windowSizeCalculation } = useWindowSize('sm', '<')
  const { isBreakpoint: isBelowBreakpointLg } = useWindowSize('lg', '<')
  const imageXlUrl = getImageUrl({ imageUrl: imageXl?.url })
  const imageMdUrl = getImageUrl({ imageUrl: imageMd?.url })
  const imageSmUrl = getImageUrl({ imageUrl: imageSm?.url })
  const imgSrc = isBelowBreakpointSm ? imageSmUrl : isBelowBreakpointLg ? imageMdUrl : imageXlUrl
  const imageAlt =
    (isBelowBreakpointSm ? imageSm?.altText : isBelowBreakpointLg ? imageMd?.altText : imageXl?.altText) ?? ''
  const showHeadlineContainer = !!headline || !!subheadline || !!(buttonGroup && buttonGroup.length > 0)

  return (
    <>
      <Container className='container'>
        <div className={cx([styles['card-full-width-container']])}>
          <div className={styles['card-full-width']}>
            {imgSrc && windowSizeCalculation && (
              <div className={styles['image-container']}>
                <Image
                  alt={imageAlt}
                  src={imgSrc}
                  width='0'
                  height='0'
                  style={{
                    width: '100%',
                    height: 'auto'
                  }}
                  sizes='100vw'
                  quality={NEXT_IMAGE_QUALITY}
                />
              </div>
            )}
            {showHeadlineContainer && (
              <div className={styles['headline-container']}>
                {headline && <Heading level={2}>{headline}</Heading>}
                {subheadline && <Heading level={6}>{subheadline}</Heading>}
                {description && <Paragraph text={description} />}
                {disclaimer && <Paragraph text={disclaimer} fontSize={'sm'} />}
                {buttonGroup && buttonGroup.length > 0 && (
                  <div className={styles['btn-container']}>
                    {buttonGroup.map((button, index: number) => (
                      <Button
                        key={`${button.title}_${index}`}
                        {...button}
                        analytics={{
                          clickTracking: `card:${headline}:${button?.title}:cta`,
                          linkPosition: getGtmLinkPosition(buttonGroup, index),
                          linkModule: 'card'
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {ctaSection && ctaSection.length > 0 && (
              <ButtonGroup btnGroupTitle={ctaSection[0].title} buttons={ctaSection.slice(1)} bottomDivider={false} />
            )}
            {cta && <Button {...cta} analytics={{ linkModule: headline }} />}
            {calendar && <Calendar isChildOfCard={true} {...calendar} />}
            {cta && <Button {...cta} analytics={{ linkModule: headline }} />}
          </div>
          {showDivider && <Divider />}
        </div>
      </Container>
    </>
  )
}

export default CardFullWidth
